
import { Component, Vue, Watch } from 'vue-property-decorator';
import i18n from '@/plugins/i18n';
import ApiHelper from '@/helpers/api-helper';
import { syncErrorMessage } from '@/helpers/sync-error-message';
import BudgetService from '@/services/BudgetService';
import FormatHelper from '@/helpers/format-helper';
import {
	FILAS_POR_PAGINA,
	VISTAS,
	consultaDe,
	estadoDe,
	fechasDelViaje,
	filtrosDeVista,
	miles,
	vistaActiva,
} from '@/helpers/budget-list';
import CustomerBudgetPdfForm from '@/components/forms/CustomerBudgetPdfForm.vue';
import CustomerBudgetForm from '@/components/forms/CustomerBudgetForm.vue';
import ActionConfirmation from '@/components/modals/ActionConfirmation.vue';
import TravelFilter from '@/components/filters/TravelFilter.vue';
import DateRangeFilter from '@/components/filters/DateRangeFilter.vue';

/** N-291 (4A): cuánto se espera tras la última tecla antes de buscar. */
const ESPERA_BUSQUEDA = 500;

/** El estado con el que nace la tabla: el de la URL (4A). */
function estadoDeLaRuta(vm: Vue) {
	return estadoDe(vm.$route && vm.$route.query ? vm.$route.query : {});
}

/**
 * N-291 (6A): el nº de un presupuesto, o null si no lo es. Lo de la dirección acaba en
 * `GET /budget/<nº>` con la sesión del panel: `resend-email%2F123` pediría otra ruta de la API (y
 * reenviaría el correo, que es un GET). Solo cifras.
 */
function numeroDeFicha(id: any): string | null {
	const texto = id === null || id === undefined ? '' : String(id);
	return /^[0-9]+$/.test(texto) ? texto : null;
}

/** N-291 (6A): el presupuesto del enlace propio (`/admin/presupuestos/<nº>`), o null. */
function fichaDeLaRuta(vm: Vue): string | null {
	return numeroDeFicha(vm.$route && vm.$route.params ? vm.$route.params.id : null);
}

function mismaConsulta(a: { [clave: string]: any }, b: { [clave: string]: any }): boolean {
	const claves = Object.keys(a);
	return claves.length === Object.keys(b).length && claves.every(clave => String(a[clave]) === String(b[clave]));
}

@Component({
	components: { ActionConfirmation, CustomerBudgetForm, CustomerBudgetPdfForm, TravelFilter, DateRangeFilter },
})
export default class CustomerBudgetTable extends Vue {
	@Watch('pagination')
	onPaginationChanged(value: string, oldValue: string) {
		this.getList();
	}

	$refs!: {
		actionConfirmation: InstanceType<typeof ActionConfirmation>;
	};

	// N-291 (4A): nace con lo que dice la URL. En la inicialización y no en `created`, para no
	// disparar el vigilante de `pagination` (la primera petición la lanza la tabla al montarse).
	private pagination: any = estadoDeLaRuta(this).opciones;
	private filters: { [clave: string]: any } = estadoDeLaRuta(this).filtros;
	private filtroProfesor: string | null = estadoDeLaRuta(this).profesor;
	private busqueda: string = estadoDeLaRuta(this).filtros.search || '';
	private esperaBusqueda: any = null;
	private lastFilters = {};
	private loading = true;
	// N-291 (6A): entrar por el enlace propio de un presupuesto abre su ficha.
	private drawer_edition = fichaDeLaRuta(this) !== null;
	private drawer_pdf = false;
	private edit_id: any = fichaDeLaRuta(this);
	private aux_id: any;
	private confirmation_type = '';
	private confirmation_message = '';

	// N-291 (5A): los números de las vistas llegan en la misma respuesta, solo cuando se piden.
	private vistas = VISTAS;
	private recuentos: { [vista: string]: number } | null = null;
	private pedirRecuentos = true;
	// N-291 (4A): cada petición lleva su número; una respuesta que llega tarde no pisa a la buena.
	private peticion = 0;

	private piePagina = { 'items-per-page-options': FILAS_POR_PAGINA };

	private opcionesIntranet = [
		{ text: i18n.t('budget_filter_intranet_all'), value: 'all' },
		{ text: i18n.t('budget_filter_intranet_synced'), value: '1' },
		{ text: i18n.t('budget_filter_intranet_not_synced'), value: '0' },
	];

	// N-291 (2A): solo se ordena por lo que el servidor sabe ordenar.
	private headers = [
		{ text: i18n.t('budget_col_number'), value: 'id', sortable: true, align: 'start' },
		{ text: i18n.t('budget_col_ordered'), value: 'created_at', sortable: true, align: 'start' },
		{ text: i18n.t('budget_col_teacher'), value: 'responsible_name_surname', sortable: true, align: 'start' },
		{ text: i18n.t('budget_col_school'), value: 'center', sortable: false, align: 'start' },
		{ text: i18n.t('budget_col_destination_dates'), value: 'start_date', sortable: true, align: 'start' },
		{ text: i18n.t('budget_col_group'), value: 'students', sortable: false, align: 'start' },
		{ text: i18n.t('budget_col_price_per_student'), value: 'price_per_student', sortable: true, align: 'start' },
		{ text: i18n.t('budget_col_intranet'), value: 'external_sync', sortable: false, align: 'start' },
		{ text: i18n.t('versions'), value: 'versions', sortable: false, align: 'center' },
		{ text: '', value: 'actions', sortable: false, align: 'end' },
	];
	private budgets: any[] = [];
	private total = 0;

	mounted() {
		if (this.drawer_edition) {
			document.body.classList.add('drawer-open');
		}
	}

	/**
	 * N-291 (6A): si la dirección cambia por fuera (pulsar «Presupuestos» en las migas, un enlace a otra
	 * ficha), la ficha la sigue. Lo que pone la propia tabla ya coincide y no hace nada.
	 */
	@Watch('$route.params.id')
	alCambiarLaFichaDeLaRuta(valor) {
		const id = numeroDeFicha(valor);
		if (!id) {
			if (this.drawer_edition) {
				this.closeDrawer();
			}
			return;
		}
		if (!this.drawer_edition || String(this.edit_id) !== String(id)) {
			this.drawer_pdf = false;
			this.edit_id = id;
			this.drawer_edition = true;
			document.body.classList.add('drawer-open');
		}
	}

	get vistaActual(): string | null {
		return vistaActiva(this.filters, new Date());
	}

	/** El destino puesto, como número: así lo tienen las opciones del selector. */
	get destino(): number | null {
		return this.filters.travel_id ? Number(this.filters.travel_id) : null;
	}

	numeroDeVista(vista: string): string {
		return this.recuentos ? miles(this.recuentos[vista]) : '';
	}

	getList(force = false) {
		if (ApiHelper.filterHasChanged(this.pagination, this.filters, this.lastFilters) || force) {
			this.loading = true;
			const peticion = ++this.peticion;
			const parametros = Object.assign({}, this.filters);
			const conRecuentos = this.pedirRecuentos;
			if (conRecuentos) {
				parametros.with_counts = 1;
				this.pedirRecuentos = false;
			}
			this.ponerLaUrl();
			BudgetService.list(this.pagination, parametros)
				.then(res => {
					if (res.status === 200) {
						if (res.data.counts) {
							this.recuentos = res.data.counts;
						}
						if (peticion !== this.peticion) {
							return; // llegó tarde: ya hay otra en camino con lo último pedido
						}
						this.budgets = res.data.elements;
						this.total = res.data.total;
						this.loading = false;
					}
				})
				.catch(error => {
					if (conRecuentos) {
						this.pedirRecuentos = true;
					}
					if (peticion === this.peticion) {
						this.loading = false;
					}
					ApiHelper.handleErrors(error, {}, this.$bus);
				});
		}
	}

	/** N-291 (4A): filtros, orden y página en la URL, sin apilar historial. */
	ponerLaUrl() {
		if (!this.$router) {
			return;
		}
		const consulta = consultaDe(this.filters, this.pagination, this.filtroProfesor);
		const actual = this.$route && this.$route.query ? this.$route.query : {};
		if (!mismaConsulta(consulta, actual)) {
			this.$router.replace({ query: consulta }).catch(() => undefined);
		}
	}

	onBusqueda(valor: string | null) {
		this.busqueda = valor || '';
		clearTimeout(this.esperaBusqueda);
		this.esperaBusqueda = setTimeout(() => {
			this.ponerFiltros({ search: this.busqueda.trim() });
		}, ESPERA_BUSQUEDA);
	}

	/** Pone (o quita, si llega vacío) uno o varios filtros y vuelve a la página 1. */
	ponerFiltros(cambios: { [clave: string]: any }) {
		Object.keys(cambios).forEach(clave => {
			const valor = cambios[clave];
			if (valor === undefined || valor === null || valor === '') {
				this.$delete(this.filters, clave);
			} else {
				this.$set(this.filters, clave, valor);
			}
		});
		if (!this.filters.by_customer_id) {
			this.filtroProfesor = null;
		}
		this.irALaPrimeraPagina();
	}

	/** N-291 (5A): una vista pone SUS filtros y quita los demás. */
	aplicarVista(vista: string) {
		clearTimeout(this.esperaBusqueda);
		this.busqueda = '';
		this.filtroProfesor = null;
		this.filters = Object.assign({ type: BudgetService.BUDGET_TYPE }, filtrosDeVista(vista, new Date()));
		this.irALaPrimeraPagina();
	}

	limpiarFiltros() {
		this.aplicarVista('all');
	}

	onEdit(id) {
		this.edit_id = id;
		this.drawer_edition = true;
		document.body.classList.add('drawer-open');
		this.ponerLaFicha(id);
	}

	onEditPDF(id) {
		// N-291 (6A): desde la cabecera de la ficha, la ficha se cierra y se abre su PDF.
		if (this.drawer_edition) {
			this.closeDrawer();
		}
		this.edit_id = id;
		this.drawer_pdf = true;
		document.body.classList.add('drawer-open');
	}

	/** N-291 (6A): el nº de la ficha abierta en la dirección (o ninguno), sin tocar los filtros. */
	ponerLaFicha(id) {
		if (!this.$router) {
			return;
		}
		const query = this.$route && this.$route.query ? this.$route.query : {};
		const params: { [clave: string]: string } = id === null ? {} : { id: String(id) };
		this.$router.replace({ name: 'Presupuestos', params, query }).catch(() => undefined);
	}

	onDownload(item) {
		// P-07: pedimos una URL de descarga firmada y temporal (sin el token del
		// admin) y navegamos a ella. Antes se pegaba el JWT en la URL (?token=),
		// que quedaba en el historial del navegador y en los logs de acceso.
		BudgetService.downloadUrl(item.id)
			.then(res => {
				window.location.href = res.data.download_url;
			})
			.catch(error => {
				ApiHelper.handleErrors(error, {}, this.$bus);
			});
	}

	onResendEmail(id) {
		this.confirmation_type = 'resend';
		this.confirmation_message = 'Are you sure you want resend this budget?';
		this.aux_id = id;
		this.$refs['actionConfirmation'].show();
	}

	/** N-246: el número de «Versiones» deja en la tabla solo los de ese profesor. */
	verLosDelProfesor(item) {
		this.$set(this.filters, 'by_customer_id', item.customer_id);
		this.filtroProfesor = item.responsible_name_surname || item.email;
		this.irALaPrimeraPagina();
	}

	quitarFiltroProfesor() {
		this.$delete(this.filters, 'by_customer_id');
		this.filtroProfesor = null;
		this.irALaPrimeraPagina();
	}

	irALaPrimeraPagina() {
		this.pagination = Object.assign({}, this.pagination, { page: 1 });
		this.getList();
	}

	onExternalSync(item) {
		this.confirmation_type = 'sync';
		this.confirmation_message = this.avisoDeSincronizar(item.chain_synced || []);
		this.aux_id = item.id;
		this.$refs['actionConfirmation'].show();
	}

	/**
	 * N-246: si otro de su CADENA (la misma solicitud, modificada) ya está en la
	 * Intranet, sincronizar este crea OTRO viaje allí. Se avisa antes, con cuál.
	 */
	avisoDeSincronizar(sincronizados: { id: number; ref: string | null }[]): string {
		if (!sincronizados.length) {
			return 'Are you sure you want to sync this budget?';
		}
		const budgets = sincronizados.map(s => 'nº ' + s.id + (s.ref ? ' (ref ' + s.ref + ')' : '')).join(', ');
		return i18n.t(sincronizados.length === 1 ? 'sync_chain_warning' : 'sync_chain_warning_many', { budgets }) as string;
	}

	onConfirm() {
		if (this.confirmation_type == 'resend'){
			this.resendEmail();
		} else if (this.confirmation_type == 'sync'){
			this.externalSync();
		}
	}

	resendEmail() {
		BudgetService.resendEmail(this.aux_id)
			.then(res => {
				if (res.status === 200) {
					this.$refs['actionConfirmation'].close();
					this.$bus.$emit('success', {
						message: i18n.t('success_resend_email'),
					});
				}
			})
			.catch(error => {
				// N-291: «Confirmar» se bloquea mientras trabaja; si falla, se libera para reintentar.
				this.$refs['actionConfirmation'].release();
				ApiHelper.handleErrors(error, {}, this.$bus);
			});
	}

	externalSync() {
		BudgetService.externalSync(this.aux_id)
			.then(res => {
				if (res.status === 200) {
					this.$refs['actionConfirmation'].close();
					this.pedirRecuentos = true; // N-291: cambia «Subidos a la Intranet»
					this.getList(true);
					this.$bus.$emit('success', {
						message: i18n.t('success_external_sync_budget'),
					});
				}
			})
			.catch(error => {
				this.$refs['actionConfirmation'].release();
				// N-73 (D): la Intranet dice EXACTAMENTE qué campo rechaza, y hasta ahora
				// eso se perdía tras el aviso genérico de `handleErrors`. Si no hay motivo
				// aprovechable se cae al camino de siempre, sin inventarse nada.
				const motivo = syncErrorMessage(error);
				if (motivo) {
					this.$bus.$emit('error', { error, message: motivo });
					return;
				}
				ApiHelper.handleErrors(error, {}, this.$bus);
			});
	}

	/** Tras guardar en un cajón: la tabla y los números (las fechas del viaje pueden cambiar). */
	alGuardar() {
		this.closeDrawer();
		this.pedirRecuentos = true;
		this.getList(true);
	}

	closeDrawer() {
		if (this.drawer_edition) {
			this.ponerLaFicha(null);
		}
		this.drawer_pdf = false;
		this.drawer_edition = false;
		document.body.classList.remove('drawer-open');
	}

	dateFormat(date) {
		return FormatHelper.formatDate(date);
	}

	fechas(item) {
		return fechasDelViaje(item.start_date, item.end_date);
	}

	/** «193,00 €»; sin precio por alumno (0 alumnos), una raya. */
	precio(valor) {
		return valor === null || valor === undefined ? '—' : FormatHelper.formatPrice(valor) + ' €';
	}
}
